/* globals PROD_ENV, ASSETS_PATH */
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import Helmet from 'react-helmet'
import { connect } from 'react-redux'
import googleTrackConversion from 'components/utils/googleConversion'
import dispatchers from 'reduxModules/home/dispatchers'
import { homeStatus } from 'reduxModules/home/'
import { homeStateSelector as stateSelector } from 'reduxModules/home/selector'
import { CurrentlyWatchingTutorialSummaryPropTypes } from 'components/generic/CourseSummary/propTypes'
import { JSONLD, Generic } from 'react-structured-data'
import BlogPostStructure from '../Blog/BlogPosts/BlogPost/propTypes'
import BannerSlideShow from '../generic/BannerSlideShow'
import BlogPosts from '../Blog/BlogPosts'
import Testimonial from '../generic/TestimonialVideos'
import MoreButton from '../generic/MoreButton'
import CourseListPlaceholder from './CourseListPlaceholder'
import CurrentlyWatching from './CurrentlyWatching'
import RecentPathways from './RecentPathways'
import LeadMagnetFunnelForm from '../Tutorials/TutorialDetail/LeadMagnetFunnelForm'
import LatestCourses from './LatestCourses'
import HomeBanner from './HomeBanner'
import FeaturedCourses from './FeaturedCourses'
import PopularCategories from './PopularCategories'

import './styles.css'

export function Home(props) {
  const {
    loadHome,
    loadBanners,
    loadSecondaryBanners,
    loadBlogPosts,
    loadTutorials,
    loadFBImages,
    loadRecentPathways,
    loadFreeTrialImage,
    loadCurrent,
    loadLatestTutorials,
    clearTutorialList,
    clearHome,
    openBannerSubscription,
    isSubscribed,
    freeTrialBanner,
    isMobile,
    metaInformation,
    fbImages,
    status,
    banners,
    bannerStatus,
    latestTutorials,
    currentlyWatching,
    loadMoreCurrent,
    recentsPaths,
    loadMoreRecentPathways,
    tutorials,
    categoryImages,
    secondaryBanners,
    blogPosts,
    isAuth
  } = props

  useEffect(() => {
    loadHome()
    loadBanners()
    loadSecondaryBanners()
    loadBlogPosts({ limit: 3 })
    loadTutorials({ pageSize: 4, featured: true })
    loadRecentPathways()
    loadCurrent()
    loadFreeTrialImage()
    loadFBImages()
    loadLatestTutorials()

    if (PROD_ENV) googleTrackConversion('home', 10)

    return () => {
      clearTutorialList()
      clearHome()
      openBannerSubscription()
    }
  }, [
    loadHome,
    loadBanners,
    loadSecondaryBanners,
    loadBlogPosts,
    loadTutorials,
    loadRecentPathways,
    loadCurrent,
    loadFreeTrialImage,
    loadFBImages,
    loadLatestTutorials,
    clearTutorialList,
    clearHome,
    openBannerSubscription
  ])

  const homeIsReady = status === homeStatus.success

  return (
    <div id="Home">
      <Helmet>
        {metaInformation
          .filter((m) => m.name === 'title')
          .map((meta) => (
            <title key={meta.value}>{meta.value}</title>
          ))}
        {metaInformation
          .filter((m) => m.name !== 'title')
          .map((meta) => (
            <meta key={meta.value} name={meta.name} content={meta.value} />
          ))}
        {fbImages.length > 0 &&
          fbImages.map(
            (fbImage) =>
              fbImage.value && (
                <meta
                  key={fbImage.value}
                  property="og:image"
                  content={fbImage.original}
                />
              )
          )}
      </Helmet>
      <HomeBanner
        banners={banners}
        homeIsReady={homeIsReady}
        isMobile={isMobile}
        freeTrialBanner={freeTrialBanner}
        isSubscribed={isSubscribed}
        bannerStatus={bannerStatus}
      />
      <LatestCourses latestTutorials={latestTutorials} />
      <div className="testimonial-holder">
        <div className="overlay" />
        <div className="wrapper">
          <h2>What Students Say About Us</h2>
          <Testimonial textOnly />
        </div>
      </div>
      {!latestTutorials ||
        (!latestTutorials.length && (
          <CourseListPlaceholder title="Latest Courses" />
        ))}
      {currentlyWatching.results?.length > 0 && (
        <div className="currently-watching">
          <CurrentlyWatching
            courses={currentlyWatching}
            loadMore={loadMoreCurrent}
          />
        </div>
      )}
      {recentsPaths.results.length > 0 && (
        <div className="recents-paths-section">
          <RecentPathways
            pathways={recentsPaths}
            loadMore={loadMoreRecentPathways}
          />
        </div>
      )}
      <FeaturedCourses tutorials={tutorials} />
      <div className="wrapper-categories wrapper">
        <h2>Popular Categories</h2>
        <PopularCategories
          categoryImages={categoryImages}
          isMobile={isMobile}
        />
      </div>
      {secondaryBanners?.length > 0 ? (
        <BannerSlideShow
          banners={secondaryBanners}
          isMobile={isMobile}
          isSecondary
        />
      ) : (
        <div className="banner-slideshow-placeholder" />
      )}
      <div className="blog-posts wrapper">
        <h2>Latest Blog Posts</h2>
        <BlogPosts blogPosts={blogPosts} showSocial={false} />
        <MoreButton url="/blog" />
      </div>
      {!isAuth && (
        <div className="funnel-home">
          <LeadMagnetFunnelForm tag="Homepage Email Lead" />
        </div>
      )}
      <JSONLD>
        <Generic
          type="organization"
          jsonldtype="Organization"
          schema={{
            url: 'https://www.stanwinstonschool.com',
            logo: `${ASSETS_PATH}global/assets/images/logo/sw-logo-schema.png`,
            sameAs: [
              'https://www.facebook.com/StanWinstonSchool',
              'https://www.instagram.com/stanwinstonschool/',
              'https://www.youtube.com/stanwinstonschool',
              'https://www.pinterest.com/stanwinston/',
              'https://www.tiktok.com/@stan_winston_school'
            ]
          }}
        />
      </JSONLD>
    </div>
  )
}

export const categoryImagesPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  image: PropTypes.shape({
    default: PropTypes.string.isRequired,
    featured: PropTypes.string.isRequired,
    original: PropTypes.string.isRequired
  }).isRequired,
  name: PropTypes.string.isRequired,
  tutorialCategorySlug: PropTypes.string
})

export const metaInformationPropType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired
})

Home.propTypes = {
  blogPosts: PropTypes.arrayOf(PropTypes.shape(BlogPostStructure)),
  isMobile: PropTypes.bool,
  fbImages: PropTypes.arrayOf(
    PropTypes.shape({
      original: PropTypes.string.isRequired
    })
  ),
  loadBlogPosts: PropTypes.func.isRequired,
  loadFBImages: PropTypes.func.isRequired,
  loadTutorials: PropTypes.func.isRequired,
  clearTutorialList: PropTypes.func.isRequired,
  clearHome: PropTypes.func.isRequired,
  loadFreeTrialImage: PropTypes.func.isRequired,
  openBannerSubscription: PropTypes.func.isRequired,
  closeBannerSubscription: PropTypes.func.isRequired,
  loadHome: PropTypes.func.isRequired,
  loadBanners: PropTypes.func.isRequired,
  loadSecondaryBanners: PropTypes.func.isRequired,
  loadRecentPathways: PropTypes.func.isRequired,
  loadCurrent: PropTypes.func.isRequired,
  loadLatestTutorials: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  categoryImages: PropTypes.arrayOf(categoryImagesPropType).isRequired,
  currentlyWatching: PropTypes.shape({
    results: PropTypes.arrayOf(
      PropTypes.shape({
        ...CurrentlyWatchingTutorialSummaryPropTypes,
        confirmWatchTutorial: PropTypes.bool
      })
    )
  }).isRequired,
  metaInformation: PropTypes.arrayOf(metaInformationPropType).isRequired,
  isSubscribed: PropTypes.bool,
  freeTrialBanner: PropTypes.shape({
    mainSubscribeTitle: PropTypes.string,
    mainFreeTrialTitle: PropTypes.string,
    bannerImage: PropTypes.shape({
      normal: PropTypes.string,
      mobile: PropTypes.string
    })
  })
}

Home.defaultProps = {
  blogPosts: [],
  fbImages: [],
  isMobile: false
}

export default connect(stateSelector, dispatchers)(Home)
